import { ApolloQueryResult } from '@apollo/client';
import { debounce } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import {
  DateRangeOutput,
  GetPaginatedChannelsQuery,
  SortOrder,
  useSubscribeToClinicUserChannelsSubscription,
  useGetPaginatedChannelsQuery,
} from 'shared/types/graphql';

interface UseGetPaginatedChannelsProps {
  clinicId: string;
  numberOfChannelsPerPage?: number;
  dateRange?: DateRangeOutput | null;
  channelStatusIds?: string[];
  shouldSkip?: boolean;
  shouldFetchCountOnly?: boolean;
}

interface UseGetPaginatedChannelsReturn {
  channelsPage: number;
  setChannelsPage: Dispatch<SetStateAction<number>>;
  channels?: GetPaginatedChannelsQuery['channels'];
  totalChannels?: number;
  refetchChannels: () => Promise<ApolloQueryResult<GetPaginatedChannelsQuery>>;
  isLoadingChannels: boolean;
  hasNextPage: boolean;
}

export const DEFAULT_ITEMS_PER_PAGE = 10;

export function useGetPaginatedChannels({
  clinicId,
  numberOfChannelsPerPage = DEFAULT_ITEMS_PER_PAGE,
  dateRange,
  channelStatusIds,
  shouldSkip,
  shouldFetchCountOnly = true,
}: UseGetPaginatedChannelsProps): UseGetPaginatedChannelsReturn {
  const [channelsPage, setChannelsPage] = useState(1);
  const { isFeatureEnabled } = useFeatureFlag();

  const isSortConversationsByLastMessageEnabled = isFeatureEnabled(FeatureFlagName.SortConversationByLastMessage);

  const queryVarsWhere = useMemo(
    () => ({
      clinicId: { equals: clinicId },
      ...(isSortConversationsByLastMessageEnabled
        ? {
            lastMessage: { createdAt: { gte: dateRange?.gte, lte: dateRange?.lte } },
          }
        : {
            updatedAt: { gte: dateRange?.gte, lte: dateRange?.lte },
          }),
      channelStatusId: channelStatusIds?.length ? { in: channelStatusIds } : undefined,
    }),
    [channelStatusIds, clinicId, dateRange?.gte, dateRange?.lte, isSortConversationsByLastMessageEnabled],
  );

  const {
    data: channelsData,
    refetch: refetchChannels,
    loading: isLoadingChannels,
  } = useGetPaginatedChannelsQuery({
    variables: {
      where: queryVarsWhere,
      take: numberOfChannelsPerPage,
      skip: (channelsPage - 1) * numberOfChannelsPerPage,
      orderBy: { lastMessage: { createdAt: SortOrder.Desc } },
      countOnly: shouldFetchCountOnly,
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !clinicId || shouldSkip,
  });
  useSubscribeToClinicUserChannelsSubscription({
    variables: {
      clinicId,
    },
    skip: !clinicId,
    onData: (d) => {
      const channelIsPartOfTheCurrentPage = channelsData?.channels?.some(
        (channel) => channel.id === d?.data?.data?.channelChanged?.node?.id,
      );

      const channelShouldBePartOfTheCurrentPage =
        channelStatusIds && channelStatusIds?.includes(d?.data?.data?.channelChanged?.node?.channelStatus?.id || '');

      if (channelIsPartOfTheCurrentPage || channelShouldBePartOfTheCurrentPage) {
        debounce(() => {
          refetchChannels();
        }, 100)();
      }
    },
  });

  const totalChannels = useMemo(() => channelsData?.count, [channelsData]);

  const hasNextPage = useMemo(() => {
    return totalChannels !== 0 && (totalChannels || 0) > channelsPage * DEFAULT_ITEMS_PER_PAGE;
  }, [channelsPage, totalChannels]);

  useEffect(() => {
    setChannelsPage(1);
  }, [clinicId]);

  return {
    channelsPage,
    setChannelsPage,
    channels: channelsData?.channels,
    totalChannels,
    refetchChannels,
    isLoadingChannels,
    hasNextPage,
  };
}
